import React, { useState } from "react";
import { Share2, Check } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const COPIED_INDICATOR_MS = 1800;

const VARIANT_STYLES = {
  solid:
    "px-3 py-2 bg-[#2A2421] text-[#F9F6F0] hover:bg-[#2A2421]/90",
  outline:
    "px-3 py-2 border border-[#2A2421]/15 text-[#2A2421] hover:bg-[#2A2421]/5",
  ghost:
    "px-2.5 py-1.5 text-[#5C544F] hover:text-[#2A2421] hover:bg-[#2A2421]/5",
};

/**
 * Universal share button.
 * - Uses native Web Share API when available (mobile / modern browsers)
 * - Falls back to copying the URL to clipboard with a toast confirmation
 */
export default function ShareButton({
  title,
  text,
  url,
  className = "",
  iconSize = 16,
  label,
  testId = "share-button",
  variant = "ghost",
}) {
  const { isRTL } = useI18n();
  const [justCopied, setJustCopied] = useState(false);

  const shareUrl =
    url || (typeof window !== "undefined" ? window.location.href : "");

  const handleShare = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const payload = {
      title: title || document.title,
      text: text || title || "",
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
    } catch (err) {
      // User cancelled or share failed — fall through to clipboard.
      if (process.env.NODE_ENV !== "production") {
        console.debug("[Share] native share unavailable, copying instead", err);
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setJustCopied(true);
      toast.success(isRTL ? "تم نسخ الرابط" : "Link copied");
      setTimeout(() => setJustCopied(false), COPIED_INDICATOR_MS);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("[Share] clipboard write failed", err);
      }
      toast.error(isRTL ? "تعذّر النسخ" : "Could not copy link");
    }
  };

  const base =
    "inline-flex items-center gap-2 rounded-full text-xs font-medium transition-colors";
  const variantClass = VARIANT_STYLES[variant] || VARIANT_STYLES.ghost;

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid={testId}
      className={`${base} ${variantClass} ${className}`}
      aria-label={label || (isRTL ? "مشاركة" : "Share")}
    >
      {justCopied ? <Check size={iconSize} /> : <Share2 size={iconSize} />}
      {label && <span>{label}</span>}
    </button>
  );
}
